import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux';
import { User } from 'common/models';
import * as authLocalStorage from './authLocalStorage';
// The following imports are for JS docs use only.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { customStatusCodes } from 'common/api/utils/customStatusCodes';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { handleExpiredSession } from 'common/api/utils/handleExpiredSession';

export const authSliceName = 'auth';

export interface AuthState {
  /** Tracks user information. */
  user: User | null;
  /**
   * The authentication token. It is held both in the store and in the client's
   * local storage.
   */
  token: string | null;
  /**
   * The flag to track whether the client has detected at least one api call
   * that returned a {@link customStatusCodes.SESSION_EXPIRED} status code.
   *
   * @remark It is turned off once the {@param endpointsToRefetch} array is
   * empty.
   */
  sessionExpired: boolean;
  /**
   * An array of endpoints to be refetched on log-in. The api calls that
   * fail with a {@link customStatusCodes.SESSION_EXPIRED} status code are
   * tracked and force-refetched to work around an elusive client-side bug.
   *
   * See {@link handleExpiredSession} for more details.
   */
  endpointsToRefetch: string[];
}

const storedAuthState = authLocalStorage.getAuthState();

const initialState: AuthState = storedAuthState?.token
  ? { ...storedAuthState, sessionExpired: false, endpointsToRefetch: [] }
  : { token: null, user: null, sessionExpired: false, endpointsToRefetch: [] };

export const authSlice = createSlice({
  name: authSliceName,
  initialState,
  reducers: {
    userLoggedIn: (
      state: AuthState,
      action: PayloadAction<Pick<AuthState, 'token' | 'user'>>,
    ) => {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
    },
    userLoggedOut: (state: AuthState) => {
      state.token = null;
      state.user = null;
    },
    setSessionExpired: (state: AuthState, action: PayloadAction<boolean>) => {
      state.sessionExpired = action.payload;
    },
    addEndpointToRefetch: (state: AuthState, action: PayloadAction<string>) => {
      state.endpointsToRefetch = [...state.endpointsToRefetch, action.payload];
    },
    removeEndpointToRefetch: (
      state: AuthState,
      action: PayloadAction<string>,
    ) => {
      state.endpointsToRefetch = state.endpointsToRefetch.filter(
        endpoint => endpoint !== action.payload,
      );

      if (state.endpointsToRefetch.length === 0) {
        state.sessionExpired = false;
      }
    },
  },
});

export const selectAuthState = (state: RootState): AuthState => state.auth;
